import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MetadataPanel = ({ archiveData }) => {
  const [copiedField, setCopiedField] = useState('');

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatAddress = (address) => {
    return `${address?.slice(0, 8)}...${address?.slice(-6)}`;
  };

  const handleExternalLink = (url) => {
    window.open(url, '_blank');
  };

  const metadataItems = [
    {
      label: 'Original URL',
      value: archiveData?.originalUrl,
      displayValue: archiveData?.originalUrl,
      icon: 'Globe',
      copyable: true,
      external: true,
      externalUrl: archiveData?.originalUrl
    },
    {
      label: 'Capture Timestamp',
      value: archiveData?.timestamp,
      displayValue: archiveData?.timestamp,
      icon: 'Clock',
      copyable: true
    },
    {
      label: 'IPFS Hash',
      value: archiveData?.ipfsHash,
      displayValue: formatAddress(archiveData?.ipfsHash),
      icon: 'Database',
      copyable: true,
      external: true,
      externalUrl: `https://gateway.pinata.cloud/ipfs/${archiveData?.ipfsHash}`
    },
    {
      label: 'Transaction Hash',
      value: archiveData?.transactionHash,
      displayValue: formatAddress(archiveData?.transactionHash),
      icon: 'Link',
      copyable: true,
      external: true,
      externalUrl: `https://polygonscan.com/tx/${archiveData?.transactionHash}`
    },
    {
      label: 'Block Number',
      value: archiveData?.blockNumber,
      displayValue: archiveData?.blockNumber?.toLocaleString(),
      icon: 'Hash',
      copyable: true
    },
    {
      label: 'Gas Used',
      value: archiveData?.gasUsed,
      displayValue: `${archiveData?.gasUsed?.toLocaleString()} MATIC`,
      icon: 'Zap',
      copyable: false
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="FileText" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Notarization Details</h3>
      </div>
      {/* Metadata Items */}
      <div className="space-y-4">
        {metadataItems?.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-4 bg-muted/30 rounded-md">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <Icon name={item?.icon} size={16} className="text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{item?.label}</p>
                <p className="text-sm text-muted-foreground truncate" title={item?.value}>
                  {item?.displayValue}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-shrink-0">
              {item?.copyable && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={copiedField === item?.label ? "Check" : "Copy"}
                  onClick={() => handleCopy(item?.value, item?.label)}
                  className={copiedField === item?.label ? "text-success" : ""}
                />
              )}
              
              {item?.external && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  onClick={() => handleExternalLink(item?.externalUrl)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Verification Status */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="ShieldCheck" size={16} className="text-success" />
            <span className="text-sm font-medium text-foreground">Verification Status</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse-subtle" />
            <span className="text-sm text-success font-medium">Verified on Blockchain</span>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground mt-2">
          This notarization has been permanently recorded on the Polygon blockchain and cannot be altered or deleted.
        </p>
      </div>
    </div>
  );
};

export default MetadataPanel;